import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mic, MicOff, ArrowLeft, Sparkles, Check, Loader2 } from 'lucide-react';
import { useVoiceInput, type UseVoiceInput } from '@/hooks/useVoiceInput';
import { chooseBuildRoute } from '@/lib/newSiteBuildRoute';
// One source of truth: this modal renders the SAME questions + persistence + prompt assembly the
// inline chat interview uses. STYLES/COLORS/persistGuidedEssentials/assembleBuildPrompt all live in
// guidedInterview.ts. persistGuidedEssentials is SITE-SCOPED (no tenant-profile write), so a build
// here can't bleed into the account's other sites.
import {
  STYLES,
  COLORS,
  persistGuidedEssentials,
  assembleBuildPrompt,
  type StyleKey,
} from '@/lib/guidedInterview';

interface GuidedStartDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
const TOTAL = 5; // style → name+market → contact → connect+color → plan

/**
 * A short, tap-to-answer onboarding for agents who don't know what to type. It
 * collects a style + name (+ optional market, contact, MLS ID, brand color),
 * persists the essentials, shows a plan, then hands a whole-site scaffold prompt
 * to the Studio (which auto-runs it). The plan promises exactly what the scaffold
 * builds. Full Brand Manager + Integrations stay in the dashboard.
 */
export default function GuidedStartDialog({ open, onOpenChange }: GuidedStartDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [style, setStyle] = useState<StyleKey | null>(null);
  const [market, setMarket] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mls, setMls] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const nameVoice = useVoiceInput((t) => setName((p) => (p ? `${p} ${t}` : t)));
  const marketVoice = useVoiceInput((t) => setMarket((p) => (p ? `${p} ${t}` : t)));

  // Restart at the first step each time the dialog opens (answers persist so an
  // accidental close doesn't lose typed input).
  useEffect(() => { if (open) setStep(0); }, [open]);

  const styleLabel = STYLES.find((s) => s.key === style)?.label ?? '';
  const next = () => setStep((s) => Math.min(s + 1, TOTAL - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  async function handleBuild() {
    setSaving(true);
    try {
      // Persist the essentials via the SHARED, SITE-SCOPED helper (contact + color → the site's
      // config; MLS → the site's integration — never the tenant profile). Best-effort.
      await persistGuidedEssentials({ style, name, market, email, phone, mls, color }).catch(() => {});
      // The Studio's site-config query may be cached — invalidate so the scaffold
      // reads the freshly-saved contact + color when it runs.
      queryClient.invalidateQueries({ queryKey: ['site-config'] });
      queryClient.invalidateQueries({ queryKey: ['site-build-state'] });
    } finally {
      setSaving(false);
      onOpenChange(false);
      navigate(chooseBuildRoute(true, assembleBuildPrompt({ style, name, market })));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-brand-midnight">
            <Sparkles className="h-5 w-5 text-brand-plum" />
            Let's build your site
          </DialogTitle>
          <DialogDescription>
            Answer a few quick questions — no tech knowledge needed.
          </DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="flex items-center gap-1.5">
          {Array.from({ length: TOTAL }).map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-brand-plum' : 'bg-brand-lilac-2'}`} />
          ))}
        </div>

        <div className="min-h-[250px] py-1">
          {step === 0 && (
            <Step title="What's the vibe?" subtitle="Pick a look — you can fine-tune everything later.">
              <div className="grid grid-cols-1 gap-2">
                {STYLES.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => { setStyle(s.key); next(); }}
                    className={`flex items-center justify-between rounded-xl border px-4 py-2.5 text-left transition-colors ${
                      style === s.key
                        ? 'border-brand-plum bg-brand-surface ring-1 ring-brand-lavender'
                        : 'border-brand-border hover:bg-brand-surface'
                    }`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-brand-midnight">{s.label}</p>
                      <p className="text-xs text-brand-ink">{s.blurb}</p>
                    </div>
                    {style === s.key && <Check className="h-4 w-4 shrink-0 text-brand-plum" />}
                  </button>
                ))}
              </div>
            </Step>
          )}

          {step === 1 && (
            <Step title="What's your business called?" subtitle="Your brokerage name, or just your own name.">
              <div className="space-y-3">
                <VoiceField value={name} onChange={setName} placeholder="e.g. Simon Gray Realty" voice={nameVoice} onEnter={next} autoFocus />
                <div>
                  <label className="mb-1 block text-xs font-semibold text-brand-ink">
                    Your market / area <span className="font-normal text-brand-ink-soft">(optional)</span>
                  </label>
                  <VoiceField value={market} onChange={setMarket} placeholder="e.g. Palm Beach, FL" voice={marketVoice} />
                </div>
              </div>
            </Step>
          )}

          {step === 2 && (
            <Step title="How can clients reach you?" subtitle="Shown on your site so buyers and sellers can contact you. Optional.">
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-brand-ink">Email</label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@brokerage.com" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-brand-ink">Phone</label>
                  <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(561) 555-0192" />
                </div>
              </div>
            </Step>
          )}

          {step === 3 && (
            <Step title="Connect & brand" subtitle="Optional — refine anytime in Integrations & Brand Manager.">
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-brand-ink">
                    MLS agent ID <span className="font-normal text-brand-ink-soft">— connects your listings</span>
                  </label>
                  <Input value={mls} onChange={(e) => setMls(e.target.value)} placeholder="e.g. 276001234" />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-brand-ink">Primary color</label>
                  <div className="flex flex-wrap items-center gap-2">
                    {COLORS.map((c) => (
                      <button
                        key={c.hex}
                        type="button"
                        title={c.label}
                        aria-label={c.label}
                        onClick={() => setColor(c.hex)}
                        className={`h-8 w-8 rounded-full transition-transform hover:scale-110 ${
                          color === c.hex ? 'ring-2 ring-brand-midnight ring-offset-2' : 'shadow'
                        }`}
                        style={{ backgroundColor: c.hex }}
                      />
                    ))}
                    <label
                      className="relative inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-2 border-dashed border-brand-lavender text-sm text-brand-plum"
                      title="Custom color"
                    >
                      +
                      <input
                        type="color"
                        value={color ?? '#4E1F60'}
                        onChange={(e) => setColor(e.target.value)}
                        className="absolute inset-0 cursor-pointer opacity-0"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </Step>
          )}

          {step === 4 && (
            <Step title="Here's the plan" subtitle="The AI builds this now — then you tweak anything by chatting.">
              <ul className="space-y-2 text-sm text-brand-midnight">
                <PlanLine>
                  A <strong>{styleLabel || 'modern'}</strong> real-estate website
                  {name.trim() ? <> for <strong>{name.trim()}</strong></> : null}
                  {market.trim() ? <> in {market.trim()}</> : null}
                </PlanLine>
                <PlanLine>Home page with a hero &amp; featured listings</PlanLine>
                <PlanLine>Search, About, and Contact pages</PlanLine>
                <PlanLine>
                  {mls.trim()
                    ? <>Your listings &amp; agents from MLS <strong>{mls.trim()}</strong></>
                    : <>Your MLS listings &amp; agents connected</>}
                </PlanLine>
                <PlanLine>
                  {(email.trim() || phone.trim())
                    ? <>Your contact details{email.trim() ? <> ({email.trim()})</> : null} on the site</>
                    : <>Your contact section</>}
                  {color ? <> + your <span style={{ color }} className="font-semibold">brand color</span></> : null}
                </PlanLine>
              </ul>
              <p className="mt-4 text-xs text-brand-ink-soft">
                Fine-tune colors, your logo &amp; listings anytime in Brand Manager and Integrations.
              </p>
            </Step>
          )}
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between border-t border-brand-border pt-3">
          <Button variant="ghost" size="sm" onClick={back} disabled={step === 0 || saving} className="gap-1 text-brand-ink">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          {step < TOTAL - 1 ? (
            <Button size="sm" onClick={next} disabled={step === 0 && !style} className="bg-brand-plum hover:bg-brand-plum-deep">
              {step === 3 ? 'See the plan' : 'Next'}
            </Button>
          ) : (
            <Button size="sm" onClick={handleBuild} disabled={saving} className="gap-1.5 bg-brand-plum hover:bg-brand-plum-deep">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {saving ? 'Setting up…' : 'Build my site'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Step({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="text-base font-semibold text-brand-midnight" style={{ fontFamily: 'Montserrat, sans-serif' }}>{title}</h3>
      {subtitle && <p className="mt-0.5 mb-3 text-sm text-brand-ink">{subtitle}</p>}
      {children}
    </div>
  );
}

function PlanLine({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal" />
      <span>{children}</span>
    </li>
  );
}

function VoiceField({
  value, onChange, placeholder, voice, onEnter, autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  voice: UseVoiceInput;
  onEnter?: () => void;
  autoFocus?: boolean;
}) {
  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) { e.preventDefault(); onEnter(); } }}
        className={voice.supported ? 'pr-10' : ''}
      />
      {voice.supported && (
        <button
          type="button"
          onClick={voice.toggle}
          aria-label={voice.recording ? 'Stop voice input' : 'Start voice input'}
          className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 transition-colors ${
            voice.recording ? 'text-brand-error animate-pulse' : 'text-brand-ink-soft hover:text-brand-plum'
          }`}
        >
          {voice.recording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}
